import { useEffect, useCallback, useRef } from 'react';
import { valveAPI, getDeviceTelemetry } from '../services/api';
import { useDevices } from '../context/DeviceContext';
import authService from '../services/auth';

const STORAGE_KEY = 'nissa-guard-historical-data';
const COLLECTION_INTERVAL = 5 * 60 * 1000; // 5 minutos
const MAX_DATA_POINTS = 30 * 24 * 12; // 30 días * 24 horas * 12 mediciones por hora (cada 5 min)

export const useDataCollection = () => {
  const intervalRef = useRef(null);
  const lastCollectionRef = useRef(0);
  const { selectedDevice } = useDevices();

  // Función para obtener datos almacenados
  const getStoredData = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error leyendo datos almacenados:', error);
      return [];
    }
  }, []);

  // Función para guardar datos
  const saveDataPoint = useCallback((dataPoint) => {
    try {
      const existingData = getStoredData();
      const newData = [...existingData, dataPoint];
      
      // Mantener solo los últimos MAX_DATA_POINTS para no llenar el localStorage
      const trimmedData = newData.slice(-MAX_DATA_POINTS);
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedData));
      console.log('✅ Datos guardados:', dataPoint);
    } catch (error) {
      console.error('Error guardando datos:', error);
    }
  }, [getStoredData]);

  // Función para recopilar datos del ESP32 o AWS
  const collectData = useCallback(async () => {
    const now = Date.now();
    
    // No recopilar si no hay dispositivo seleccionado
    if (!selectedDevice) {
      console.log('⚠️ No hay dispositivo seleccionado para recopilar datos');
      return;
    }

    // En demostración no se recopila nada: estos datos alimentan los informes
    // históricos y no deben mezclarse con los simulados, además de que la
    // llamada real sólo devolvería un 401.
    if (authService.isDemo()) return;

    /* BUG: aquí se pasaba `selectedDevice` —el OBJETO— donde la API espera un
       identificador, así que las URLs salían como
       `/devices/[object Object]/valve/status` y fallaban siempre. Los puntos
       guardados en localStorage también llevaban el objeto en `deviceId`. */
    const deviceId = selectedDevice.device_id;
    
    // Evitar recopilaciones muy frecuentes
    if (now - lastCollectionRef.current < COLLECTION_INTERVAL - 1000) {
      return;
    }

    try {
      console.log(`🔄 Recopilando datos del dispositivo ${deviceId}...`);
      
      // Obtener datos en paralelo
      const [telemetryData, valveData] = await Promise.all([
        getDeviceTelemetry(deviceId, 1).catch(e => ({ data: [], error: e.message })),
        valveAPI.getStatus(deviceId).catch(e => ({ valve_state: null, error: e.message }))
      ]);

      // Extraer datos de telemetría
      const telemetry = telemetryData.data && telemetryData.data.length > 0 
        ? telemetryData.data[0] 
        : {};

      // Crear punto de datos
      const dataPoint = {
        timestamp: new Date().toISOString(),
        flow: telemetry.flow_liters || 0,
        flowRate: telemetry.flow_rate_lpm || 0,
        valveState: valveData.valve_state || telemetry.valve_state || 'unknown',
        deviceId,
        flowError: telemetryData.error || null,
        valveError: valveData.error || null,
        collectionTime: now
      };

      // Guardar datos
      saveDataPoint(dataPoint);
      lastCollectionRef.current = now;

    } catch (error) {
      console.error('Error en recopilación de datos:', error);
      
      // Guardar punto de error para mantener continuidad temporal
      const errorPoint = {
        timestamp: new Date().toISOString(),
        flow: 0,
        flowRate: 0,
        valveState: 'error',
        deviceId: selectedDevice.device_id,
        flowError: error.message,
        valveError: error.message,
        collectionTime: now
      };
      
      saveDataPoint(errorPoint);
      lastCollectionRef.current = now;
    }
  }, [saveDataPoint, selectedDevice]);

  // Función para obtener estadísticas de los datos recopilados
  const getDataStats = useCallback(() => {
    const data = getStoredData();
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const last24h = data.filter(d => new Date(d.timestamp) >= oneDayAgo);
    const lastWeek = data.filter(d => new Date(d.timestamp) >= oneWeekAgo);
    
    return {
      total: data.length,
      last24h: last24h.length,
      lastWeek: lastWeek.length,
      oldestRecord: data.length > 0 ? data[0].timestamp : null,
      newestRecord: data.length > 0 ? data[data.length - 1].timestamp : null,
      hasData: data.length > 0
    };
  }, [getStoredData]);

  // Función para procesar datos para reportes
  const getProcessedDataForReports = useCallback((days = 30) => {
    const data = getStoredData();
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    
    // Filtrar datos del período solicitado
    const periodData = data.filter(d => new Date(d.timestamp) >= cutoffDate);
    
    if (periodData.length === 0) {
      return { realData: [], hasRealData: false, dataPoints: 0 };
    }

    // Agrupar por día
    const dailyData = {};
    
    periodData.forEach(point => {
      const date = new Date(point.timestamp).toISOString().split('T')[0];
      
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          flowReadings: [],
          valveChanges: 0,
          valveStates: [],
          errors: 0
        };
      }
      
      dailyData[date].flowReadings.push(point.flow);
      dailyData[date].valveStates.push(point.valveState);
      
      if (point.flowError || point.valveError) {
        dailyData[date].errors++;
      }
    });

    // Procesar datos diarios
    const processedData = Object.values(dailyData).map(day => {
      const validFlowReadings = day.flowReadings.filter(f => f > 0);
      const totalFlow = validFlowReadings.reduce((sum, f) => sum + f, 0);
      
      // Contar cambios de válvula (transiciones de estado)
      let valveChanges = 0;
      for (let i = 1; i < day.valveStates.length; i++) {
        if (day.valveStates[i] !== day.valveStates[i - 1]) {
          valveChanges++;
        }
      }
      
      const avgFlowRate = validFlowReadings.length > 0 
        ? validFlowReadings.reduce((sum, f) => sum + f, 0) / validFlowReadings.length 
        : 0;
      
      const openStates = day.valveStates.filter(s => s === 'open').length;
      const totalStates = day.valveStates.length;
      const valveOpenHours = totalStates > 0 ? (openStates / totalStates) * 24 : 0;

      return {
        date: day.date,
        consumption: Math.round(totalFlow * 100) / 100,
        valveChanges: Math.max(valveChanges, 1), // Mínimo 1 para evitar 0s
        valveOpenHours: Math.round(valveOpenHours * 100) / 100,
        avgFlowRate: Math.round(avgFlowRate * 100) / 100,
        dataPoints: day.flowReadings.length,
        errors: day.errors,
        isRealData: true
      };
    });

    return {
      realData: processedData.sort((a, b) => new Date(a.date) - new Date(b.date)),
      hasRealData: true,
      dataPoints: periodData.length,
      daysWithData: processedData.length
    };
  }, [getStoredData]);

  // Función para limpiar datos antiguos
  const cleanOldData = useCallback(() => {
    try {
      const data = getStoredData();
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      
      const cleanedData = data.filter(d => new Date(d.timestamp) >= thirtyDaysAgo);
      
      if (cleanedData.length !== data.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedData));
        console.log(`🧹 Limpiados ${data.length - cleanedData.length} registros antiguos`);
      }
    } catch (error) {
      console.error('Error limpiando datos antiguos:', error);
    }
  }, [getStoredData]);

  // Iniciar recopilación automática
  useEffect(() => {
    // Recopilar inmediatamente al montar
    collectData();
    
    // Limpiar datos antiguos al iniciar
    cleanOldData();
    
    // Configurar intervalo de recopilación
    intervalRef.current = setInterval(() => {
      collectData();
      
      // Limpiar datos antiguos cada 100 recopilaciones (~8 horas)
      if (Math.random() < 0.01) {
        cleanOldData();
      }
    }, COLLECTION_INTERVAL);

    console.log(`🚀 Sistema de recopilación iniciado (cada ${COLLECTION_INTERVAL / 1000 / 60} minutos)`);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        console.log('⏹️ Sistema de recopilación detenido');
      }
    };
  }, [collectData, cleanOldData]);

  return {
    getStoredData,
    getDataStats,
    getProcessedDataForReports,
    collectData, // Para recopilación manual
    cleanOldData // Para limpieza manual
  };
};

export default useDataCollection;
